import { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { typography } from '@icebreaker/shared/design';
import { LOCALES, type Locale } from '@icebreaker/shared/domain';
import { Button, PageShell } from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { ChangePasswordButton } from '@/features/access/ui/change-password-button';
import { SessionsButton } from '@/features/access/ui/sessions-button';
import { fetchOverview } from '@/features/admin/store/admin.slice';
import { signOut } from '@/features/auth/store/auth.slice';
import { selectStaff, selectVisibleScreenRoutes } from '@/features/auth/store/auth.selectors';
import { fetchSalesCounts } from '@/features/sales/store/sales-inquiries.slice';
import { useLocale, useTranslation } from '@/shared/i18n/i18n-provider';
import { useTheme } from '@/shared/theme/theme-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { BrandLock } from './brand-lock';
import styles from './admin-shell.module.css';

/**
 * PANEL KABUĞU — ink üst şerit + yan gezinme.
 *
 * Web uygulamasının kromuyla (`site-nav`) PAYLAŞILMAZ: o krom kullanıcının
 * gezinmesidir, bu iç aracın. Ortak olan şey KİTTİR (`PageShell`, `BrandLock`
 * gövdesi, `Button`) — kabuk onları çağırır, kendi bileşenini yazmaz.
 *
 * Ekran içerikleri `<Outlet/>` ile gelir; kabuk kapının İÇİNDE çizilir, yani
 * yetkisiz bir oturum burayı hiç görmez (`StaffGate`).
 *
 * ROZETLER (web'deki `/admin` kabuğundan taşındı): üst şeritteki açık kuyruk
 * sayacı ve yan gezinme rozetleri KPI ucundan beslenir. Sayı YOKSA rozet de
 * yoktur — sıfır/tahmin basılmaz (CLAUDE.md md. 9). Kurum doğrulama kuyruğu
 * KPI setinde taşınmadığı için o satır rozet TAŞIMAZ.
 */
export function AdminShell() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const staff = useAppSelector(selectStaff);
  const { locale, setLocale } = useLocale();
  const { mode, setPreference: setThemePreference } = useTheme();
  /* Düğme İKİ YÖNLÜ: açıktayken karanlığa, karanlıktayken açığa geçirir. */
  const nextTheme = mode === 'dark' ? 'light' : 'dark';
  const overviewPhase = useAppSelector((state: RootState) => state.admin.overview.phase);
  const openCount = useAppSelector(
    (state: RootState) => state.admin.overview.data?.kpis.openReports ?? null,
  );
  const registeredTotal = useAppSelector((state: RootState) => state.admin.users.registeredTotal);
  const newSalesCount = useAppSelector((state: RootState) => state.panelSales.newCount);

  useEffect(() => {
    if (overviewPhase === 'idle') {
      dispatch(fetchOverview.request());
    }
  }, [dispatch, overviewPhase]);

  /**
   * YAN GEZİNME — panelin EKRAN ENVANTERİ, YETKİYLE SÜZÜLMÜŞ.
   *
   * `SCREEN_ROUTES` ile birebir eşleşmek ZORUNDA (`route-reachability.guard`):
   * linksiz ekran teslim sayılmaz, kayıtsız bağlantı da kırar.
   *
   * SÜZGEÇ (QA S2/S3 · P2-1): kullanılamayacak yetenek LİSTELENMEZ. `moderator`
   * "Kurum doğrulama" bağlantısını adminle birebir aynı görüyordu; tıkladığında
   * sunucu 403 veriyor ve ekran "Kuyruk yüklenemedi · Tekrar dene" çiziyordu.
   * Karar `selectVisibleScreenRoutes`tan gelir — yetki tablosu burada YENİDEN
   * YAZILMAZ.
   */
  const visibleRoutes = useAppSelector(selectVisibleScreenRoutes);
  const canSeeSales = visibleRoutes.includes(ROUTES.salesInquiries);

  /*
   * YENİ ADAY ROZETİ (panel küçük işler 1 · B-01). Sayı ucu kişi taşımaz ve
   * denetim satırı yazmaz; kabuk açılışta ve pencereye dönüşte okur, böylece
   * personel başka ekrandayken gelen başvuruyu sekmeye döndüğünde görür.
   * Ekranı göremeyen personel (yetkisiz) için istek hiç gitmez.
   */
  useEffect(() => {
    if (!canSeeSales) return undefined;
    const load = () => dispatch(fetchSalesCounts.request());
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [canSeeSales, dispatch]);
  const allLinks: readonly {
    readonly to: string;
    readonly label: string;
    readonly badge: string | null;
  }[] = [
    /*
     * GENEL GÖRÜNÜM (kullanıcı kararı 2026-09-02 §3) — panelin kökü ve ilk
     * bağlantısı. Rozet TAŞIMAZ: pano tek bir sayaç değil, yedi sayı gösterir;
     * hangisinin rozet olacağı uydurma bir seçim olurdu.
     */
    { to: ROUTES.home, label: t.t('admin.nav.overview'), badge: null },
    {
      to: ROUTES.users,
      label: t.t('admin.nav.users'),
      badge: registeredTotal > 0 ? t.formatNumber(registeredTotal, { notation: 'compact' }) : null,
    },
    { to: ROUTES.moderation, label: t.t('admin.nav.moderation'), badge: badgeOf(openCount) },
    { to: ROUTES.reports, label: t.t('admin.nav.reports'), badge: badgeOf(openCount) },
    { to: ROUTES.verifications, label: t.t('orgVerificationAdmin.navLabel'), badge: null },
    /*
     * SATIŞ ADAYLARI (kurum satışla açılır · P1, rozet B-01). Rozet "Yeni"
     * durumundaki aday sayısıdır ve yalnız sayı > 0 iken çizilir; sayı
     * bilinmiyorsa (`null`) ya da sıfırsa rozet yoktur (uydurma sayaç yok,
     * md. 9). Kaynak liste değil, kişi taşımayan sayı ucudur.
     * KURUMLAR rozet taşımaz.
     */
    {
      to: ROUTES.salesInquiries,
      label: t.t('admin.nav.salesInquiries'),
      badge: newSalesCount !== null && newSalesCount > 0 ? t.formatNumber(newSalesCount) : null,
    },
    { to: ROUTES.organizations, label: t.t('admin.nav.organizations'), badge: null },
    /*
     * REFERANS KODLARI (P7): Satış adayları ve Kurumlar'ın yanında durur.
     * Yalnız `staff:credit_manage` taşıyan personel görür (süzgeç aşağıda).
     * Rozet TAŞIMAZ: "kaç aktif kod var" bir iş kuyruğu değildir.
     */
    { to: ROUTES.referralCodes, label: t.t('admin.nav.referralCodes'), badge: null },
    /*
     * E-POSTA GÖNDERİMLERİ — salt okunur defter. Rozet TAŞIMAZ: "kaç başarısız
     * var" sayısı KPI ucunda yoktur ve uydurulmaz (CLAUDE.md md. 9).
     */
    { to: ROUTES.emailDeliveries, label: t.t('admin.nav.emailDeliveries'), badge: null },
    /*
     * PERSONEL (panel kimliği ayrımı · Karar 4c): davet, yeniden davet, askı.
     * Yalnız `staff:account_manage` taşıyan personel görür (süzgeç aşağıda).
     */
    { to: ROUTES.staff, label: t.t('admin.nav.staff'), badge: null },
  ];
  const links = allLinks.filter((link) => visibleRoutes.includes(link.to));

  const nextLocale: Locale = LOCALES[(LOCALES.indexOf(locale) + 1) % LOCALES.length] ?? locale;

  return (
    <>
      <header className={styles.topbarBand} data-chrome="app">
        <PageShell rail="app" className={styles.topbar}>
          {/*
            PLATFORM KİLİDİ (marka sözleşmesi §7 satır 11): panel kromu da
            organizatör paneliyle AYNI dili konuşur — 26px koyu bant kilidi +
            rol eki. Marka adı token katmanından gelir, ekrana yazılmaz.
          */}
          <BrandLock
            className={styles.topbarBrand}
            square={26}
            wordmark={typography.fontSize.lg}
            tone="surface"
            href={ROUTES.home}
            suffix={<span className={styles.topbarSuffix}>{t.t('admin.brandSuffix')}</span>}
          />
          {openCount !== null ? (
            <span className={styles.topbarMeta}>
              {t.t('admin.queueBadge', { count: openCount })}
            </span>
          ) : null}
          <span className={styles.topbarAccount}>{staff?.account.email ?? ''}</span>
          <span className={styles.topbarTools}>
            <button
              type="button"
              className={styles.langButton}
              onClick={() => setLocale(nextLocale)}
            >
              {t.t('language.current', { name: t.t(`language.${nextLocale}`) })}
            </button>
            {/*
              Tema anahtarı: panel açık temada AÇILIR, karanlık buradan seçilir ve
              seçim korunur. Dil düğmesiyle aynı kalıp — etiket, basınca GEÇİLECEK
              tercihi yazar.
            */}
            <button
              type="button"
              className={styles.langButton}
              onClick={() => setThemePreference(nextTheme)}
            >
              {t.t('theme.current', { name: t.t(`theme.${nextTheme}`) })}
            </button>
            {/*
              Hesap eylemleri (oturumlar · şifre değiştirme) — yan gezinme YÖNETİM
              ekranlarınındır; personelin KENDİ hesabına dair her şey buradadır.
            */}
            <SessionsButton />
            <ChangePasswordButton />
            <Button variant="ghost" size="sm" onClick={() => dispatch(signOut.request())}>
              {t.t('admin.panel.signOut')}
            </Button>
          </span>
        </PageShell>
      </header>
      <PageShell rail="app" className={styles.shell}>
        <nav className={styles.sidebar} aria-label={t.t('admin.nav.heading')}>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                [styles.sideLink, isActive ? styles.sideLinkActive : ''].filter(Boolean).join(' ')
              }
            >
              <span className={styles.sideLinkLabel}>{link.label}</span>
              {link.badge ? <span className={styles.sideBadge}>{link.badge}</span> : null}
            </NavLink>
          ))}
        </nav>
        <main className={styles.content}>
          <Outlet />
        </main>
      </PageShell>
    </>
  );
}

/** Sayı yoksa rozet de yok: uydurma sayaç basılmaz (CLAUDE.md md. 9). */
function badgeOf(count: number | null): string | null {
  return count === null ? null : String(count);
}
